/**
 * The Overview's figures for one period (DEC-28 C2).
 *
 * Who was asked and how they answered, how long the answer took and how much
 * of that was this service, why cards were not approved, Plug and Charge,
 * where the sessions went, what waits to be sent, and every switch. Counts
 * only: no token, no correlation id.
 *
 * A section of the Overview rather than a page of its own: the health cards
 * below it are what an operator checks when something is wrong, and this is
 * what they check every morning.
 */

import { useEffect, useState } from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'
import {
  Chip,
  KeyValueList,
  KeyValueRow,
  KpiCard,
  SectionCard,
  StatusBadge,
  formatTimestamp,
} from '../platform'
import { api } from '../api'
import type { Dashboard, Latency } from '../api'
import { count, milliseconds, shareOf, whyRows } from './totalsReading'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  wide: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  rows: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  line: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  track: {
    display: 'flex',
    height: '22px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  own: { backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand, paddingLeft: tokens.spacingHorizontalXS, whiteSpace: 'nowrap', fontSize: tokens.fontSizeBase200 },
  wait: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorNeutralForeground1, paddingLeft: tokens.spacingHorizontalXS, whiteSpace: 'nowrap', fontSize: tokens.fontSizeBase200 },
  thin: { height: '8px', borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorNeutralBackground3, overflow: 'hidden' },
  fill: { height: '8px', backgroundColor: tokens.colorBrandBackground },
  muted: { color: tokens.colorNeutralForeground3 },
  switches: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
})

const RANGES: [Dashboard['range'], string][] = [
  ['today', 'Today'],
  ['7d', '7 days'],
  ['30d', '30 days'],
]

const LANE_NAMES: Record<string, string> = {
  dcs: 'DCS, through Service OCPI',
  tim: 'TIM',
  hubject: 'Hubject connector',
  wall_e_list: 'Wall-E list',
}

const OUTCOME_STATUS = { APPROVED: 'success', BLOCKED: 'error', UNKNOWN: 'warning' } as const

export default function OverviewDashboard() {
  const styles = useStyles()
  const [range, setRange] = useState<Dashboard['range']>('7d')
  const [data, setData] = useState<Dashboard | null>(null)

  useEffect(() => {
    api
      .get<Dashboard>(`/dashboard?range=${range}`)
      // A service a deploy behind answers another shape; the section is then
      // absent rather than broken, like every other section here.
      .then((answer) => setData(Array.isArray(answer?.lanes) ? answer : null))
      .catch(() => setData(null))
  }, [range])

  if (!data) return null
  const { totals } = data
  const slowest = Math.max(1, ...data.lanes.map((l) => l.latency.p50 ?? 0))
  const refusals = whyRows(data.refusals, 8)
  const widestRefusal = Math.max(1, ...refusals.map((r) => r.count))

  return (
    <section aria-label="This period" className={styles.root}>
      <div className={styles.head}>
        <div className={styles.chips} role="group" aria-label="Period">
          {RANGES.map(([value, label]) => (
            <Chip key={value} selected={range === value} onClick={() => setRange(value)}>
              {label}
            </Chip>
          ))}
        </div>
        <div className={styles.switches} aria-label="Switches">
          {data.switches.map((s) => (
            <StatusBadge key={s.name} status={s.on ? 'success' : 'info'} label={`${s.label}: ${s.on ? 'on' : 'off'}`} />
          ))}
        </div>
      </div>

      <div className={styles.grid}>
        <KpiCard title="Asks" value={count(totals.asks)} caption="every ask, the second of a pair included" />
        <KpiCard title="Approved" value={count(totals.APPROVED)} caption={shareOf(totals.APPROVED, totals.asks)} />
        <KpiCard title="Unknown" value={count(totals.UNKNOWN)} caption={shareOf(totals.UNKNOWN, totals.asks)} />
        <KpiCard title="Blocked" value={count(totals.BLOCKED)} caption={shareOf(totals.BLOCKED, totals.asks)} />
      </div>

      <div className={styles.grid}>
        {data.lanes.map((lane) => {
          const asked = lane.by_outcome.APPROVED + lane.by_outcome.BLOCKED + lane.by_outcome.UNKNOWN
          return (
            <SectionCard
              key={lane.lane}
              title={LANE_NAMES[lane.lane] ?? lane.lane}
              action={
                <StatusBadge
                  status={asked === 0 ? 'info' : lane.last_failure_at ? 'warning' : 'success'}
                  label={asked === 0 ? 'Nothing asked' : lane.last_failure_at ? 'Answered, with failures' : 'Answering'}
                />
              }
            >
              <KeyValueList>
                <KeyValueRow label="Approved" value={count(lane.by_outcome.APPROVED)} />
                <KeyValueRow label="Blocked, unknown" value={`${count(lane.by_outcome.BLOCKED)}, ${count(lane.by_outcome.UNKNOWN)}`} />
                <KeyValueRow label="Median answer" value={milliseconds(lane.latency.p50)} />
                <KeyValueRow
                  label="Last failure"
                  value={lane.last_failure_at ? formatTimestamp(lane.last_failure_at) : 'none in this period'}
                />
              </KeyValueList>
            </SectionCard>
          )
        })}
      </div>

      <SectionCard
        title="Answer time, and where it goes"
        description="Median per lane, split into this service's own time and the wait for the authority it records. On the default route, own time includes waiting for Hubject when it is asked beside Service OCPI."
      >
        <div className={styles.rows}>
          {data.lanes.filter((l) => l.latency.p50 !== null).map((lane) => (
            <LatencyRow key={lane.lane} label={LANE_NAMES[lane.lane]} latency={lane.latency} slowest={slowest} />
          ))}
        </div>
      </SectionCard>

      <div className={styles.wide}>
        <SectionCard title="Not approved, and why">
          {refusals.length === 0 ? (
            <span className={styles.muted}>Nothing was refused in this period.</span>
          ) : (
            <div className={styles.rows}>
              {refusals.map((row) => (
                <div key={`${row.outcome}|${row.label}`}>
                  <div className={styles.line}>
                    <span>
                      <StatusBadge status={OUTCOME_STATUS[row.outcome as keyof typeof OUTCOME_STATUS] ?? 'info'} label={row.outcome} />{' '}
                      {row.label}
                    </span>
                    <span>{count(row.count)}</span>
                  </div>
                  <div className={styles.thin} aria-hidden="true">
                    <div className={styles.fill} style={{ width: `${Math.max(2, (row.count * 100) / widestRefusal)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        <SectionCard title="Plug and Charge" description="Asks whose identifier is an eMAID.">
          <KeyValueList divided>
            <KeyValueRow label="Asks" value={count(data.plug_and_charge.asks)} />
            <KeyValueRow label="Contracts" value={count(data.plug_and_charge.contracts)} />
            <KeyValueRow label="Median answer" value={milliseconds(data.plug_and_charge.latency.p50)} />
            {data.plug_and_charge.by_authority.map((row) => (
              <KeyValueRow
                key={`${row.authority}|${row.outcome}`}
                label={`${LANE_NAMES[row.authority ?? ''] ?? row.authority ?? 'nobody'}, ${row.outcome.toLowerCase()}`}
                value={`${count(row.count)} ${row.count === 1 ? 'ask' : 'asks'}, ${count(row.contracts)} ${row.contracts === 1 ? 'contract' : 'contracts'}`}
              />
            ))}
          </KeyValueList>
        </SectionCard>
      </div>

      <div className={styles.wide}>
        <SectionCard title="Where the sessions went" description="Starts and stops as the OCPP Server sent them, and where each start went.">
          <KeyValueList divided>
            <KeyValueRow label="Started" value={count(data.sessions.started)} />
            <KeyValueRow label="Stopped" value={count(data.sessions.stopped)} />
            <KeyValueRow label="To Service OCPI, billed there" value={count(data.sessions.to_ocpi)} />
            <KeyValueRow label="To TIM, settled with BMW" value={count(data.sessions.to_tim)} />
            <KeyValueRow label="To the Hubject connector" value={count(data.sessions.to_hubject)} />
            <KeyValueRow label="Kept here, no payer" value={count(data.sessions.kept)} />
          </KeyValueList>
        </SectionCard>
        <SectionCard title="Waiting to be sent" description="Now, whatever the period.">
          <KeyValueList divided>
            <QueueRow label="TIM settlements" state={data.queues.tim_settlements} />
            <QueueRow label="Hubject starts and stops" state={data.queues.hubject_forwards} />
          </KeyValueList>
        </SectionCard>
      </div>
    </section>
  )
}

function LatencyRow({ label, latency, slowest }: { label: string; latency: Latency; slowest: number }) {
  const styles = useStyles()
  const own = Math.max(0, latency.own_p50 ?? 0)
  const wait = Math.max(0, latency.wait_p50 ?? 0)
  const scale = (value: number) => `${Math.min(100, (value * 100) / slowest)}%`
  return (
    <div>
      <div className={styles.line}>
        <span>{label}</span>
        <span>
          {milliseconds(latency.p50)}, 95% within {milliseconds(latency.p95)}
        </span>
      </div>
      <div className={styles.track} role="img" aria-label={`${label}: ${milliseconds(own)} this service, ${milliseconds(wait)} the authority`}>
        <div className={styles.own} style={{ width: scale(own) }}>
          {milliseconds(own)}
        </div>
        <div className={styles.wait} style={{ width: scale(wait) }}>
          {milliseconds(wait)}
        </div>
      </div>
    </div>
  )
}

function QueueRow({ label, state }: { label: string; state: Dashboard['queues']['tim_settlements'] }) {
  const waiting = state.waiting
    ? `${count(state.waiting)} waiting, the oldest since ${formatTimestamp(state.oldest_waiting_at ?? '')}`
    : 'nothing waiting'
  return (
    <KeyValueRow
      label={label}
      value={state.failed ? `${waiting}; ${count(state.failed)} failed, for a person` : waiting}
    />
  )
}
